import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, X, CheckCircle2, Eye, EyeOff, History, FileText, Users } from "lucide-react";

const TABS = [
    { k: "config", label: "Operator & Config" },
    { k: "policies", label: "Policies" },
    { k: "acceptances", label: "Acceptances" },
];

export default function AdminLegal() {
    const [tab, setTab] = useState("config");
    return (
        <div>
            <h1 className="font-heading text-2xl font-extrabold text-navy">Legal & Policies</h1>
            <p className="text-sm text-navy/55">Configure the legal operator, publish & version policies, and track acceptances.</p>
            <div className="mt-4 flex gap-2 border-b border-navy/10">
                {TABS.map((t) => (
                    <button key={t.k} data-testid={`legal-tab-${t.k}`} onClick={() => setTab(t.k)}
                        className={`-mb-px border-b-2 px-3 py-2 text-sm font-bold transition-colors ${tab === t.k ? "border-orange text-orange" : "border-transparent text-navy/50"}`}>{t.label}</button>
                ))}
            </div>
            <div className="mt-5">
                {tab === "config" && <ConfigTab />}
                {tab === "policies" && <PoliciesTab />}
                {tab === "acceptances" && <AcceptancesTab />}
            </div>
        </div>
    );
}

function ConfigTab() {
    const [cfg, setCfg] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get("/admin/legal/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
    if (!cfg) return <Loader2 className="h-6 w-6 animate-spin text-orange" />;
    const set = (k, v) => setCfg({ ...cfg, [k]: v });
    const save = async () => {
        setBusy(true);
        try {
            const payload = { ...cfg, service_areas: Array.isArray(cfg.service_areas) ? cfg.service_areas : String(cfg.service_areas).split(",").map((s) => s.trim()).filter(Boolean), min_age: Number(cfg.min_age) };
            const { data } = await api.put("/admin/legal/config", payload);
            setCfg(data); toast.success("Legal operator config saved");
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
    };
    const F = [
        ["operator", "Legal Operator"], ["brand", "Brand"], ["support_email", "Support Email"],
        ["support_phone", "Support Phone"], ["city", "Launch City"], ["current_version", "Current Policy Version"],
    ];
    return (
        <div className="max-w-lg space-y-3">
            <div className="rounded-xl bg-amber-50 p-3 text-xs text-amber-700">Changing the operator updates all policies dynamically — no rebuild needed. Confidential tax/bank identifiers are never stored or shown here.</div>
            {F.map(([k, l]) => (
                <div key={k}><label className="mb-1 block text-xs font-semibold text-navy/60">{l}</label>
                    <input data-testid={`cfg-${k}`} value={cfg[k] || ""} onChange={(e) => set(k, e.target.value)} className="w-full rounded-xl border border-navy/15 px-3 py-2.5 text-sm outline-none focus:border-orange" /></div>
            ))}
            <div><label className="mb-1 block text-xs font-semibold text-navy/60">Service Areas (comma separated)</label>
                <input data-testid="cfg-areas" value={Array.isArray(cfg.service_areas) ? cfg.service_areas.join(", ") : cfg.service_areas} onChange={(e) => set("service_areas", e.target.value.split(",").map((s) => s.trim()))} className="w-full rounded-xl border border-navy/15 px-3 py-2.5 text-sm outline-none focus:border-orange" /></div>
            <div><label className="mb-1 block text-xs font-semibold text-navy/60">Minimum Age</label>
                <input data-testid="cfg-min_age" type="number" value={cfg.min_age} onChange={(e) => set("min_age", e.target.value)} className="w-32 rounded-xl border border-navy/15 px-3 py-2.5 text-sm outline-none focus:border-orange" /></div>
            <button data-testid="cfg-save" disabled={busy} onClick={save} className="flex items-center gap-2 rounded-full bg-orange px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />} Save config</button>
        </div>
    );
}

function PoliciesTab() {
    const [rows, setRows] = useState(null);
    const [edit, setEdit] = useState(null);
    const [versions, setVersions] = useState(null);
    const load = () => api.get("/admin/legal/policies").then((r) => setRows(r.data)).catch(() => setRows([]));
    useEffect(() => { load(); }, []);

    const act = async (id, path, body) => {
        try { await api.post(`/admin/legal/policies/${id}/${path}`, body || {}); toast.success("Updated"); load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    const newVersion = async (p) => {
        const version = prompt("New version label", p.version + ".1");
        if (!version) return;
        try {
            await api.post("/admin/legal/policies", { slug: p.slug, number: p.number, title: p.title, category: p.category, content: p.content, version, effective_date: new Date().toISOString().slice(0, 10), status: "draft" });
            toast.success("Draft version created"); load();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };

    if (rows === null) return <Loader2 className="h-6 w-6 animate-spin text-orange" />;
    return (
        <div>
            <div className="overflow-hidden rounded-2xl border border-navy/10 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-navy/[0.03] text-left text-xs uppercase tracking-wide text-navy/50">
                        <tr><th className="px-3 py-2.5">#</th><th className="px-3 py-2.5">Policy</th><th className="px-3 py-2.5">Version</th><th className="px-3 py-2.5">Status</th><th className="px-3 py-2.5">Active</th><th className="px-3 py-2.5">Actions</th></tr>
                    </thead>
                    <tbody>
                        {rows.map((p) => (
                            <tr key={p.id} className="border-t border-navy/5" data-testid={`policy-row-${p.slug}`}>
                                <td className="px-3 py-2.5 font-bold text-navy">{String(p.number).padStart(2, "0")}</td>
                                <td className="px-3 py-2.5 text-navy/80">{p.title}<div className="text-[11px] text-navy/40">{p.category}</div></td>
                                <td className="px-3 py-2.5 text-navy/60">{p.version}</td>
                                <td className="px-3 py-2.5"><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${p.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{p.status}</span></td>
                                <td className="px-3 py-2.5">{p.active ? <CheckCircle2 size={16} className="text-emerald-500" /> : <span className="text-navy/30">—</span>}</td>
                                <td className="px-3 py-2.5">
                                    <div className="flex flex-wrap gap-1.5">
                                        <button data-testid={`policy-edit-${p.slug}`} onClick={() => setEdit(p)} className="rounded-full border border-navy/15 px-2.5 py-1 text-[11px] font-bold text-navy hover:border-orange hover:text-orange">Edit</button>
                                        {p.status !== "published" && <button data-testid={`policy-publish-${p.slug}`} onClick={() => act(p.id, "publish")} className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">Publish</button>}
                                        <button data-testid={`policy-active-${p.slug}`} onClick={() => act(p.id, "active", { active: !p.active })} className="rounded-full border border-navy/15 px-2.5 py-1 text-[11px] font-bold text-navy">{p.active ? <EyeOff size={12} /> : <Eye size={12} />}</button>
                                        <button onClick={() => newVersion(p)} className="rounded-full border border-navy/15 px-2.5 py-1 text-[11px] font-bold text-navy">New ver</button>
                                        <button onClick={() => api.get(`/admin/legal/policies/slug/${p.slug}/versions`).then((r) => setVersions({ slug: p.slug, list: r.data }))} className="rounded-full border border-navy/15 px-2 py-1 text-[11px] text-navy/60"><History size={12} /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {edit && <EditDrawer policy={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
            {versions && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4" onClick={() => setVersions(null)}>
                    <div className="max-h-[70vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
                        <h3 className="mb-3 font-heading font-bold text-navy">Version history · {versions.slug}</h3>
                        {versions.list.map((v) => (
                            <div key={v.id} className="flex items-center justify-between border-b border-navy/5 py-2 text-sm">
                                <span className="text-navy/70">{v.version} · <span className="text-navy/40">{v.status}{v.active ? " · active" : ""}</span></span>
                                <span className="text-xs text-navy/40">{v.effective_date}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function EditDrawer({ policy, onClose, onSaved }) {
    const [f, setF] = useState({ title: policy.title, category: policy.category, content: policy.content, effective_date: policy.effective_date || "", version: policy.version });
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try { await api.put(`/admin/legal/policies/${policy.id}`, f); toast.success("Policy saved"); onSaved(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-navy/40" onClick={onClose}>
            <div className="h-full w-full max-w-xl overflow-y-auto bg-white p-6" onClick={(e) => e.stopPropagation()}>
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="font-heading text-lg font-extrabold text-navy">Edit · {policy.title}</h2>
                    <button onClick={onClose}><X size={20} className="text-navy/50" /></button>
                </div>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className="mb-1 block text-xs font-semibold text-navy/60">Title</label><input data-testid="edit-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full rounded-xl border border-navy/15 px-3 py-2 text-sm outline-none focus:border-orange" /></div>
                        <div><label className="mb-1 block text-xs font-semibold text-navy/60">Category</label><input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="w-full rounded-xl border border-navy/15 px-3 py-2 text-sm outline-none focus:border-orange" /></div>
                        <div><label className="mb-1 block text-xs font-semibold text-navy/60">Version</label><input value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} className="w-full rounded-xl border border-navy/15 px-3 py-2 text-sm outline-none focus:border-orange" /></div>
                        <div><label className="mb-1 block text-xs font-semibold text-navy/60">Effective date</label><input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} className="w-full rounded-xl border border-navy/15 px-3 py-2 text-sm outline-none focus:border-orange" /></div>
                    </div>
                    <div><label className="mb-1 block text-xs font-semibold text-navy/60">Content (markdown — supports tokens like {"{{OPERATOR}}"})</label>
                        <textarea data-testid="edit-content" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} rows={16} className="w-full rounded-xl border border-navy/15 px-3 py-2 font-mono text-xs outline-none focus:border-orange" /></div>
                    <button data-testid="edit-save" disabled={busy} onClick={save} className="flex items-center gap-2 rounded-full bg-orange px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />} Save</button>
                </div>
            </div>
        </div>
    );
}

function AcceptancesTab() {
    const [s, setS] = useState(null);
    useEffect(() => { api.get("/admin/legal/acceptances/stats").then((r) => setS(r.data)).catch(() => setS(false)); }, []);
    if (s === null) return <Loader2 className="h-6 w-6 animate-spin text-orange" />;
    if (!s) return <p className="text-sm text-navy/50">No data.</p>;
    return (
        <div className="space-y-5">
            <div className="flex gap-4">
                <div className="rounded-2xl border border-navy/10 bg-white p-4"><div className="flex items-center gap-2 text-navy/50"><Users size={15} /> <span className="text-xs font-semibold uppercase">Total acceptances</span></div><p data-testid="accept-total" className="mt-1 font-heading text-3xl font-extrabold text-navy">{s.total}</p></div>
            </div>
            <div>
                <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-navy/40">By policy version</h3>
                <div className="overflow-hidden rounded-2xl border border-navy/10 bg-white">
                    {s.by_version.map((v) => (<div key={v.version} className="flex items-center justify-between border-b border-navy/5 px-4 py-2.5 text-sm last:border-0"><span className="font-semibold text-navy">{v.version}</span><span className="font-bold text-orange">{v.count}</span></div>))}
                    {s.by_version.length === 0 && <p className="px-4 py-3 text-sm text-navy/40">No acceptances yet.</p>}
                </div>
            </div>
            <div>
                <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-navy/40">Recent</h3>
                <div className="overflow-hidden rounded-2xl border border-navy/10 bg-white">
                    {s.recent.map((r, i) => (<div key={i} className="flex items-center justify-between border-b border-navy/5 px-4 py-2.5 text-sm last:border-0"><span className="text-navy/80">{r.user}</span><span className="text-xs text-navy/40">{r.version} · {new Date(r.accepted_at).toLocaleString()}</span></div>))}
                    {s.recent.length === 0 && <p className="px-4 py-3 text-sm text-navy/40">No acceptances yet.</p>}
                </div>
            </div>
        </div>
    );
}
